import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, Plus, ArrowRight, Cylinder,
  Truck, Check, Box
} from 'lucide-react';
import dashboardApi from '../../api/dashboardApi';
import {
  checkCylinderMasterHasData,
  getAllCylindersForStats
} from '../../api/cylinderMasterApi';
import './CylinderDashboard.css';

const CylinderDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [stockByType, setStockByType] = useState([]);
  const [recentMovement, setRecentMovement] = useState([]);

  const calculateTestingDue = (cylinders) => {
    if (!cylinders || cylinders.length === 0) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    return cylinders.filter(c => {
      if (!c.testingDate) return false;
      const testingDate = new Date(c.testingDate);
      return testingDate <= thirtyDaysFromNow;
    }).length;
  };

  const calculateStatusCounts = (cylinders) => {
    if (!cylinders || cylinders.length === 0) {
      return { total: 0, available: 0, withCustomer: 0, inTransit: 0 };
    }
    
    const counts = { total: cylinders.length, available: 0, withCustomer: 0, inTransit: 0 };
    
    cylinders.forEach(c => {
      const status = c.currentStatus;
      if (status === 'IN_STOCK' || status === 'IN_INVENTORY') {
        counts.available++;
      } else if (status === 'OUT_WITH_CUSTOMER' || status === 'ISSUED') {
        counts.withCustomer++;
      } else if (status === 'IN_TRANSIT') {
        counts.inTransit++;
      }
    });
    
    return counts;
  };

  const formatStatValue = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString();
  };

  const getStatSubtext = (label, value, hasCylinderMasterData) => {
    if (label === 'Available') {
      if (!hasCylinderMasterData) return 'N/A - Master not used';
      if (stats?.total > 0) {
        const pct = stats.total > 0 ? ((value / stats.total) * 100).toFixed(1) : '0.0';
        return `${pct}% of stock`;
      }
      return '0% of stock';
    }
    if (label === 'Testing Due') {
      if (!hasCylinderMasterData) return 'N/A - Master not used';
      return value > 0 ? 'Needs attention' : 'All current';
    }
    return 'Updated now';
  };

  const getStatSubClass = (label, value) => {
    if (label === 'Available') return 'green';
    if (label === 'Testing Due') return value > 0 ? 'orange' : '';
    return '';
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [masterCheck, productWiseReport, recentChallans] = await Promise.all([
        checkCylinderMasterHasData(),
        dashboardApi.getProductWiseReport(),
        dashboardApi.getRecentChallans({ size: 20 })
      ]);
      
      const masterData = masterCheck.data || masterCheck;
      const hasCylinderMasterData = masterData?.totalElements > 0;
      
      // Calculate stats from product-wise report and cylinder master
      const productData = productWiseReport.data || productWiseReport || [];
      const challanData = recentChallans.data || recentChallans || {};
      const challans = challanData.content || challanData || [];
      
      // Total from delivered challans
      const totalDelivered = productData.reduce((sum, p) => sum + (p.deliveredQty || 0), 0);
      const totalReceived = productData.reduce((sum, p) => sum + (p.receivedQty || 0), 0);
      
      // Received today - count received challans from today
      const today = new Date().toISOString().split('T')[0];
      const receivedToday = challans.filter(c => 
        c.challanType === 'RECEIVED' && c.date === today
      ).reduce((sum, c) => sum + (c.items?.reduce((s, i) => s + (i.receivedQty || 0), 0) || 0), 0);
      
      let cylinderStats = { total: 0, available: 0, withCustomer: 0, inTransit: 0 };
      let cylinders = [];
      let testingDue = 0;
      let stockByTypeData = [];
      
      if (hasCylinderMasterData) {
        const allCylindersResponse = await getAllCylindersForStats();
        const allCylindersData = allCylindersResponse.data || allCylindersResponse;
        cylinders = allCylindersData.content || allCylindersData || [];
        
        cylinderStats = calculateStatusCounts(cylinders);
        testingDue = calculateTestingDue(cylinders);
        
        // Build stock by type using both cylinder master status AND product-wise report
        const typeMap = {};
        cylinders.forEach(c => {
          const type = c.cylinderName || c.cylinderCapacity || 'Unknown';
          if (!typeMap[type]) {
            typeMap[type] = {
              type,
              total: 0,
              delivered: 0,
              received: 0,
              withCustomer: 0,
              available: 0,
              inTransit: 0,
              testingDue: 0
            };
          }
          typeMap[type].total++;
          
          const status = c.currentStatus;
          if (status === 'IN_STOCK' || status === 'IN_INVENTORY') {
            typeMap[type].available++;
          } else if (status === 'OUT_WITH_CUSTOMER' || status === 'ISSUED') {
            typeMap[type].withCustomer++;
          } else if (status === 'IN_TRANSIT') {
            typeMap[type].inTransit++;
          }
          
          if (c.testingDate) {
            const testingDate = new Date(c.testingDate);
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);
            const thirtyDaysFromNow = new Date(todayDate);
            thirtyDaysFromNow.setDate(todayDate.getDate() + 30);
            if (testingDate <= thirtyDaysFromNow) {
              typeMap[type].testingDue++;
            }
          }
        });
        
        // Merge product-wise report data
        productData.forEach(p => {
          if (typeMap[p.productName]) {
            typeMap[p.productName].delivered = p.deliveredQty;
            typeMap[p.productName].received = p.receivedQty;
          } else {
            typeMap[p.productName] = {
              type: p.productName,
              total: p.deliveredQty,
              delivered: p.deliveredQty,
              received: p.receivedQty,
              withCustomer: p.deliveredQty - p.receivedQty,
              available: 0,
              inTransit: 0,
              testingDue: 0
            };
          }
        });
        
        stockByTypeData = Object.values(typeMap);
      } else {
        // No cylinder master - use only product-wise report
        cylinderStats.total = totalDelivered;
        cylinderStats.available = null;
        cylinderStats.withCustomer = totalDelivered - totalReceived;
        cylinderStats.inTransit = 0;
        testingDue = 0;
        
        stockByTypeData = productData.map(p => ({
          type: p.productName,
          total: p.deliveredQty,
          delivered: p.deliveredQty,
          received: p.receivedQty,
          withCustomer: p.deliveredQty - p.receivedQty,
          available: null,
          inTransit: 0,
          testingDue: 0
        }));
      }
      
      // Build recent movement from actual challans
      const recentMovements = challans.slice(0, 10).flatMap(c => {
        const movements = [];
        const date = c.date ? new Date(c.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
        const docNo = c.challanNumber || c.systemChallanNumber || '';
        const customer = c.customer?.name || c.customerName || '';
        
        if (c.items) {
          c.items.forEach(item => {
            if ((c.challanType === 'DELIVERED' || c.challanType === 'COMBINED') && item.deliveredQty > 0) {
              movements.push({
                date,
                doc: docNo,
                customer,
                cylType: item.name || item.type || '',
                qty: item.deliveredQty,
                movement: 'Delivered',
                status: c.status || 'Completed'
              });
            }
            if ((c.challanType === 'RECEIVED' || c.challanType === 'COMBINED') && item.receivedQty > 0) {
              movements.push({
                date,
                doc: docNo,
                customer,
                cylType: item.name || item.type || '',
                qty: item.receivedQty,
                movement: 'Received',
                status: c.status || 'Completed'
              });
            }
          });
        }
        return movements;
      }).slice(0, 10);
      
      setStats({
        total: cylinderStats.total,
        available: cylinderStats.available,
        withCustomer: cylinderStats.withCustomer,
        inTransit: cylinderStats.inTransit,
        receivedToday,
        testingDue,
        hasCylinderMasterData
      });
      
      setStockByType(stockByTypeData);
      setRecentMovement(recentMovements);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats({
        total: 0,
        available: null,
        withCustomer: 0,
        inTransit: 0,
        receivedToday: 0,
        testingDue: 0,
        hasCylinderMasterData: false
      });
      setStockByType([]);
      setRecentMovement([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="cyl-dash-container">
        <div className="cyl-stats-grid">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="cyl-stat-card">
              <div className="cyl-stat-label">Loading...</div>
              <div className="cyl-stat-value">—</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const flowSteps = [
    { label: 'Opening Stock', value: stats.total, Icon: Cylinder },
    { label: 'Delivered', value: stats.total > 0 ? Math.round(stats.total * 0.15) : 0, Icon: Truck },
    { label: 'With Customer', value: stats.withCustomer, Icon: Cylinder, sub: 'Active custody' },
    { label: 'Received', value: stats.total > 0 ? Math.round(stats.total * 0.12) : 0, Icon: Check },
    { label: 'Available Stock', value: stats.available ?? 0, Icon: Box, highlight: true },
  ];

  return (
    <div className="cyl-dash-container">
      {/* HEADER */}
      <div className="cyl-dash-header">
        <div>
          <div className="cyl-breadcrumb">Cylinder Management / Dashboard</div>
          <h1 className="cyl-title">Cylinder Dashboard</h1>
          <p className="cyl-subtitle">Monitor cylinder inventory, movement and customer balances.</p>
        </div>
        <div className="cyl-dash-actions">
          <button className="btn-export"><Download size={16}/> Export</button>
          <button className="btn-primary"><Plus size={16}/> Record movement</button>
        </div>
      </div>

      {/* TOP 6 STATS */}
      <div className="cyl-stats-grid">
        <div className="cyl-stat-card">
          <div className="cyl-stat-label">Total Cylinders</div>
          <div className="cyl-stat-value">{formatStatValue(stats.total)}</div>
          <div className="cyl-stat-sub">{getStatSubtext('Total', stats.total, stats.hasCylinderMasterData)}</div>
        </div>
        <div className="cyl-stat-card">
          <div className="cyl-stat-label">Available</div>
          <div className="cyl-stat-value">{formatStatValue(stats.available)}</div>
          <div className={`cyl-stat-sub ${getStatSubClass('Available', stats.available)}`}>
            {getStatSubtext('Available', stats.available, stats.hasCylinderMasterData)}
          </div>
        </div>
        <div className="cyl-stat-card">
          <div className="cyl-stat-label">With Customers</div>
          <div className="cyl-stat-value">{formatStatValue(stats.withCustomer)}</div>
          <div className="cyl-stat-sub">{getStatSubtext('With Customers', stats.withCustomer, stats.hasCylinderMasterData)}</div>
        </div>
        <div className="cyl-stat-card">
          <div className="cyl-stat-label">In Transit</div>
          <div className="cyl-stat-value">{formatStatValue(stats.inTransit)}</div>
          <div className="cyl-stat-sub">{getStatSubtext('In Transit', stats.inTransit, stats.hasCylinderMasterData)}</div>
        </div>
        <div className="cyl-stat-card">
          <div className="cyl-stat-label">Received Today</div>
          <div className="cyl-stat-value">{formatStatValue(stats.receivedToday)}</div>
          <div className="cyl-stat-sub">{getStatSubtext('Received Today', stats.receivedToday, stats.hasCylinderMasterData)}</div>
        </div>
        <div className={`cyl-stat-card ${stats.testingDue > 0 ? 'alert' : ''}`}>
          <div className="cyl-stat-label">Testing Due</div>
          <div className="cyl-stat-value">{formatStatValue(stats.testingDue)}</div>
          <div className={`cyl-stat-sub ${getStatSubClass('Testing Due', stats.testingDue)}`}>
            {getStatSubtext('Testing Due', stats.testingDue, stats.hasCylinderMasterData)}
          </div>
        </div>
      </div>

      {/* MOVEMENT FLOW */}
      <div className="cyl-card">
        <div className="cyl-card-title"><h3>CYLINDER MOVEMENT FLOW</h3></div>
        <div className="cyl-flow-grid">
          {flowSteps.map((step, idx) => (
            <>
              <div key={step.label} className={`cyl-flow-box ${step.highlight? 'highlight-green' : ''}`}>
                <div className="cyl-flow-icon"><step.Icon size={16} /></div>
                <div className="cyl-flow-content">
                  <div className="cyl-flow-label">{step.label}</div>
                  {step.sub && <div className="cyl-flow-subtext">{step.sub}</div>}
                  <div className="cyl-flow-value">{formatStatValue(step.value)}</div>
                </div>
              </div>
              {idx < flowSteps.length - 1 && <div className="cyl-flow-arrow"><ArrowRight size={14} /></div>}
            </>
          ))}
        </div>
      </div>

      {/* CYLINDER STOCK BY TYPE */}
      <section className="cyl-card cyl-table-card">
        <div className="cyl-card-header">
          <h3 className="cyl-card-heading">CYLINDER STOCK BY TYPE</h3>
          <button
            type="button"
            className="cyl-link"
            onClick={() => navigate('/dashboard/inventory/cylinders')}
          >
            View inventory <ArrowRight size={13} />
          </button>
        </div>

        <div className="cyl-table-wrap">
          <table className="cyl-table">
            <thead>
              <tr>
                <th>CYLINDER TYPE</th><th>TOTAL</th><th>DELIVERED</th><th>RECEIVED</th>
                <th>WITH CUSTOMER</th><th>AVAILABLE</th><th>TESTING DUE</th><th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {stockByType.map((r) => (
                <tr key={r.type}>
                  <td className="link">{r.type}</td>
                  <td>{formatStatValue(r.total)}</td>
                  <td>{formatStatValue(r.delivered)}</td>
                  <td>{formatStatValue(r.received)}</td>
                  <td>{formatStatValue(r.withCustomer)}</td>
                  <td>{formatStatValue(r.available)}</td>
                  <td><span className="testing-badge"><span className="dot"></span>{formatStatValue(r.testingDue)}</span></td>
                  <td className="more" aria-label={`Actions for ${r.type}`}>...</td>
                </tr>
              ))}
              {stockByType.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                    No cylinder data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* RECENT CYLINDER MOVEMENT */}
      <section className="cyl-card cyl-table-card">
        <div className="cyl-card-header">
          <h3 className="cyl-card-heading">RECENT CYLINDER MOVEMENT</h3>
          <button
            type="button"
            className="cyl-link"
            onClick={() => navigate('/dashboard/challans/view')}
          >
            View all movement <ArrowRight size={13} />
          </button>
        </div>

        <div className="cyl-table-wrap">
          <table className="cyl-table cyl-movement-table">
            <thead>
              <tr>
                <th>DATE</th><th>DOCUMENT #</th><th>CUSTOMER</th><th>CYLINDER TYPE</th>
                <th>QTY</th><th>MOVEMENT TYPE</th><th>STATUS</th><th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {recentMovement.map((m, i) => (
                <tr key={`${m.doc}-${i}`}>
                  <td className="link blue">{m.date}</td>
                  <td>{m.doc}</td><td>{m.customer}</td><td>{m.cylType}</td>
                  <td>{m.qty}</td><td>{m.movement}</td>
                  <td>
                    <span className={`status-pill ${m.status === 'Completed' ? 'completed' : 'transit'}`}>
                      <span className="dot"></span>{m.status}
                    </span>
                  </td>
                  <td className="more" aria-label={`Actions for ${m.doc}`}>...</td>
                </tr>
              ))}
              {recentMovement.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                    No recent movement data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CylinderDashboard;